import { type LocalDate, ZoneId } from "@js-joda/core";
import {
	queryOptions,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQueries,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { create, keyResolver } from "@yornaath/batshit";
import type {
	CreatePostDto,
	DeleteScheduledPostsDto,
	GenerateBlogSeoDto,
	GeneratePostsDto,
	PostDto,
} from "shared";
import { type Platform, PostStatus } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const usePostsActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const generate = useMutation({
		mutationFn: async (x: GeneratePostsDto) => {
			await Api.GeneratePosts(x);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Posts have been generated",
			});
		},
	});
	const generateBlog = useMutation({
		mutationFn: async (x: GeneratePostsDto) => {
			await Api.GenerateBlogPosts(x);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Blog posts have been generated",
			});
		},
	});

	const create = useMutation({
		mutationFn: async (x: CreatePostDto) => {
			await Api.CreatePost(x);
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Posts have been created",
			});
		},
	});

	const update = useMutation({
		mutationFn: async ({ id, dto }: { id: number; dto: CreatePostDto }) => {
			await Api.UpdatePost(id, dto);
		},
		onSuccess: async (_, { id }) => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.Single(companyId, id),
			});
			// The edit may have changed this post's scheduled date, which can
			// change its position in the by-date queue. Invalidate so any
			// reader of "what's next after this post" (e.g. the Next button)
			// refetches rather than navigating off a stale pointer.
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Posts.Next(companyId, id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Post has been updated",
			});
		},
	});

	const remove = useMutation({
		mutationFn: async (ID: number) => {
			await Api.DeletePost(ID);
		},
		onSuccess: async (_, ID) => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			queryClient.removeQueries({
				queryKey: QueryKeys.Posts.Single(companyId, ID),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Post has been deleted",
			});
		},
	});

	const removeAllScheduled = useMutation({
		mutationFn: async (x: DeleteScheduledPostsDto) => {
			await Api.DeleteScheduledPosts(x);
		},
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});

			emit({
				type: AlertType.SUCCESS,
				content:
					"All scheduled posts from the selected range have been deleted!",
			});
		},
	});

	const rewrite = useMutation({
		mutationFn: async ({
			id,
			instructions,
		}: {
			id: number;
			instructions: string;
		}) => {
			const { data } = await Api.RewritePost(id, instructions);
			return data.content;
		},
	});

	const refine = useMutation({
		mutationFn: async ({
			content,
			instructions,
			platform,
			hasMedia,
		}: {
			content: string;
			instructions: string;
			platform: Platform;
			hasMedia?: boolean;
		}) => {
			const { data } = await Api.RefineContent(
				content,
				instructions,
				platform,
				hasMedia,
			);
			return data.content;
		},
	});

	const generateBlogSeo = useMutation({
		mutationFn: async (dto: GenerateBlogSeoDto) => {
			const { data } = await Api.GenerateBlogSeo(dto);
			return data;
		},
	});

	const submitForApproval = useMutation({
		mutationFn: async (id: number) => {
			const { data } = await Api.SubmitPostForApproval(id);
			return data;
		},
		onSuccess: async (_, id) => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.Single(companyId, id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Post has been submitted for approval",
			});
		},
	});

	const approvePost = useMutation({
		mutationFn: async (id: number) => {
			const { data } = await Api.ApprovePost(id);
			return data;
		},
		onSuccess: async (_, id) => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.Single(companyId, id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Post has been approved",
			});
		},
	});

	const rejectPost = useMutation({
		mutationFn: async ({ id, reason }: { id: number; reason?: string }) => {
			const { data } = await Api.RejectPost(id, reason);
			return data;
		},
		onSuccess: async (_, { id }) => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.Single(companyId, id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Post has been rejected",
			});
		},
	});

	const bulkApprovePosts = useMutation({
		mutationFn: async (ids: number[]) => {
			const { data } = await Api.BulkApprovePosts(ids);
			return data;
		},
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Posts.All(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Posts have been approved",
			});
		},
	});

	return {
		create,
		generate,
		generateBlog,
		update,
		remove,
		removeAllScheduled,
		rewrite,
		refine,
		generateBlogSeo,
		submitForApproval,
		approvePost,
		rejectPost,
		bulkApprovePosts,
	};
};

/**
 * BATCH REQUESTS
 */

const batcher = create({
	fetcher: async (IDs: number[]) => (await Api.GetPostsBatch({ IDs })).data,
	resolver: keyResolver("id"),
});

export const useSuspensePostsList = () => {
	const { id: companyID } = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Posts.List(companyID),
		queryFn: () => Api.GetPostsList().then((res) => res.data),
	});
};

export const useSuspensePostsListRange = (from: LocalDate, to: LocalDate) => {
	const { id: companyID } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Posts.ListRange(companyID, from, to),
		queryFn: async () =>
			(
				await Api.GetPostsListRange(
					ZoneId.systemDefault().toString(),
					from.toString(),
					to.toString(),
				)
			).data,
	});

	return data;
};

export const useSuspensePostWithSiblings = (id: number) => {
	const { id: companyID } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Posts.WithSiblings(companyID, id),
		queryFn: async () => (await Api.GetPostWithSiblings(id)).data,
	});
	return data;
};

export const useNextPost = (id: number | undefined) => {
	const { id: companyID } = useCompany();
	const { data, refetch } = useQuery({
		queryKey: QueryKeys.Posts.Next(companyID, id ?? 0),
		queryFn: async () => (await Api.GetNextPost(id as number)).data,
		// The same form drives post creation, where there is no post yet and
		// so nothing to advance from. Without this the create page would
		// request /api/calendar/0/next on every mount.
		enabled: Boolean(id),
	});

	return {
		nextPost: data ?? null,
		// Callers that navigate off this value after a save (which may have
		// changed this post's scheduled date, and so its position in the
		// by-date queue) should refetch and use the fresh result rather than
		// the value captured at render time.
		refetchNextPost: async () => (await refetch()).data ?? null,
	};
};

export const useSuspensePostsLatest = () => {
	const { id: companyID } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Posts.Latest(companyID),
		queryFn: async () => (await Api.GetPostsLatest()).data,
	});
	return data;
};

export const useSuspensePostsRecent = () => {
	const { id: companyID } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Posts.Recent(companyID),
		queryFn: async () => (await Api.GetPostsRecent()).data,
	});
	return data;
};

export const useSuspensePublishingQueue = (platform: Platform | string) => {
	const { id: companyID } = useCompany();
	const { data } = useSuspenseQuery({
		queryKey: QueryKeys.Posts.PublishingQueue(companyID, platform),
		queryFn: async () => (await Api.GetPublishingQueue(platform)).data,
	});
	return data;
};

const LIVE_POST_REFETCH_MS = 10_000;
const DEFAULT_POST_STALE_MS = 5 * 60 * 1_000;

function isLivePost(post: PostDto | null | undefined): boolean {
	if (!post) {
		return false;
	}
	if (
		post.status === PostStatus.IN_PROGRESS ||
		post.publishStatus === "retrying"
	) {
		return true;
	}
	if (post.status === PostStatus.SCHEDULED && post.scheduledAt) {
		return Date.parse(post.scheduledAt) <= Date.now();
	}
	return false;
}

function postQueryOptions(ID: number | null | undefined, companyID: number) {
	return queryOptions({
		queryKey: QueryKeys.Posts.Single(companyID, ID),
		queryFn: async () => {
			if (!ID) {
				return null;
			}
			return batcher.fetch(ID);
		},
		staleTime: (query) =>
			isLivePost(query.state.data) ? 0 : DEFAULT_POST_STALE_MS,
		refetchInterval: (query) =>
			isLivePost(query.state.data) ? LIVE_POST_REFETCH_MS : false,
		refetchIntervalInBackground: true,
	});
}

export function useSuspensePost<T extends number | null | undefined>(ID: T) {
	const { id: companyID } = useCompany();
	const { data } = useSuspenseQuery(postQueryOptions(ID, companyID));

	return data as T extends number ? PostDto : null;
}

export const useSuspensePosts = (IDs: number[]) => {
	const { id: companyID } = useCompany();
	const queries = useSuspenseQueries({
		queries: IDs.map((id) => postQueryOptions(id, companyID)),
	});

	const data = queries.reduce<PostDto[]>((acc, val) => {
		if (val.data) {
			acc.push(val.data);
		}
		return acc;
	}, []);

	return data;
};
